// components/shared/TablePagination.tsx
import { Table } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface TablePaginationProps<T> {
  table: Table<T>;
  noun?: string; // "crew" | "project" | "ticket" etc
}

export const TablePagination = <T,>({
  table,
  noun = 'row',
}: TablePaginationProps<T>) => {
  const { pageIndex, pageSize } = table.getState().pagination;
  const total = table.getFilteredRowModel().rows.length;
  const pages = table.getPageCount();
  const from = total === 0 ? 0 : pageIndex * pageSize + 1;
  const to = Math.min((pageIndex + 1) * pageSize, total);
  const plural = (n: number) => `${n} ${noun}${n !== 1 ? 's' : ''}`;

  const navButtons = [
    {
      icon: ChevronsLeft,
      action: () => table.setPageIndex(0),
      disabled: !table.getCanPreviousPage(),
    },
    {
      icon: ChevronLeft,
      action: () => table.previousPage(),
      disabled: !table.getCanPreviousPage(),
    },
    {
      icon: ChevronRight,
      action: () => table.nextPage(),
      disabled: !table.getCanNextPage(),
    },
    {
      icon: ChevronsRight,
      action: () => table.setPageIndex(pages - 1),
      disabled: !table.getCanNextPage(),
    },
  ];

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-1">
      <p className="text-xs text-muted-foreground">
        {total === 0 ? (
          `No ${noun}s`
        ) : (
          <>
            Showing{' '}
            <span className="font-medium text-foreground">
              {from}–{to}
            </span>{' '}
            of{' '}
            <span className="font-medium text-foreground">{plural(total)}</span>
          </>
        )}
      </p>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            Per page
          </span>
          <Select
            value={String(pageSize)}
            onValueChange={(v) => table.setPageSize(Number(v))}
          >
            <SelectTrigger className="h-8 w-16 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[10, 20, 30, 50].map((s) => (
                <SelectItem key={s} value={String(s)} className="text-xs">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <span className="text-xs text-muted-foreground whitespace-nowrap">
          Page{' '}
          <span className="font-medium text-foreground">{pageIndex + 1}</span>{' '}
          of <span className="font-medium text-foreground">{pages}</span>
        </span>
        <div className="flex items-center gap-1">
          {navButtons.map(({ icon: Icon, action, disabled }, i) => (
            <Button
              key={i}
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={action}
              disabled={disabled}
            >
              <Icon className="h-3.5 w-3.5" />
            </Button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Typed aliases so call sites stay clean ────────────────────────────────────
export const CrewTablePagination = ({ table }: { table: Table<any> }) => (
  <TablePagination table={table} noun="crew" />
);

export const ProjectTablePagination = ({ table }: { table: Table<any> }) => (
  <TablePagination table={table} noun="project" />
);
